import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, MapPin, Phone, Mail } from 'lucide-react';
import { db } from '../shared/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useI18n } from '../i18n';
import { LanguageSelector } from '../components/LanguageSelector';
import { LocationData } from '../utils/api';
import { getWidgetUrl } from '../utils/widget';
import { applyRestaurantPageSeo, clearRestaurantPageSeo, TEN_SEAT_MARKETING_URL } from '../utils/seo';

/**
 * Microsite / fallback widget page for locations without a full website.
 * Redesigned as single-column layout inspired by SevenRooms.
 */

const DEBUG = (import.meta as any)?.env?.VITE_DEBUG_MICROSITE === 'true';
const log = (...args: any[]) => { 
  if (DEBUG) console.log('[Microsite]', ...args);
  // Always log important errors/warnings
  if (args[0] && typeof args[0] === 'string' && (args[0].includes('Error') || args[0].includes('Failed'))) {
    console.error('[Microsite]', ...args);
  }
};

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function fullAddress(loc: any): string {
  const a = loc?.address || {};
  const blocks = [
    a.street,
    [a.postal_code, a.city].filter(Boolean).join(' '),
    a.state,
    a.country,
  ]
    .map((s: string) => (s || '').trim())
    .filter(Boolean);
  return blocks.join(', ');
}

const DAY_KEYS: string[] = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

function getTodayKey(timezone?: string): string {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: timezone || 'Europe/Brussels' });
    return dtf.format(new Date()).toLowerCase(); // e.g., "monday"
  } catch {
    // Fallback to local TZ
    return ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date().getDay()];
  }
}

interface Shift {
  name?: string;
  open: string;
  close: string;
}

function renderDayHours(dayKey: string, loc: any): { shifts: Shift[] } | 'Closed' | '—' {
  const info = loc?.operating_hours?.[dayKey];
  if (!info) return '—';
  if (info.is_closed) return 'Closed';
  if (Array.isArray(info.shifts) && info.shifts.length > 0) {
    // Filter out invalid shifts but keep all valid ones
    const validShifts = info.shifts.filter((s: any) => {
      const isValid = s && s.open && s.close;
      if (!isValid && DEBUG) {
        log(`[renderDayHours] Filtered out invalid shift for ${dayKey}:`, s);
      }
      return isValid;
    });
    if (DEBUG && validShifts.length !== info.shifts.length) {
      log(`[renderDayHours] ${dayKey}: ${info.shifts.length} total shifts, ${validShifts.length} valid shifts`);
    }
    if (validShifts.length === 0) return '—';
    return { shifts: validShifts };
  }
  if (info.open && info.close) {
    return { shifts: [{ open: info.open, close: info.close }] };
  }
  return '—';
}

const Home: React.FC = () => {
  const { t, formatTime } = useI18n();
  const { slug } = useParams<{ slug: string }>();
  const [restaurant, setRestaurant] = useState<any | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const widgetIframeRef = useRef<HTMLIFrameElement>(null);
  const widgetIframeMobileRef = useRef<HTMLIFrameElement>(null);
  const { locale } = useI18n();

  // Get day titles from translations - memoized to prevent recreation on every render
  const DAY_TITLES: string[] = useMemo(() => [
    t('days.monday'),
    t('days.tuesday'),
    t('days.wednesday'),
    t('days.thursday'),
    t('days.friday'),
    t('days.saturday'),
    t('days.sunday'),
  ], [t]);

  // Use ref to track loading state and prevent concurrent loads
  const isLoadingRef = useRef(false);

  useEffect(() => {
    // Guard against concurrent loads and ensure slug is valid
    if (!slug || isLoadingRef.current) {
      if (!slug) {
        setNotFound(true);
        setLoading(false);
      }
      return;
    }

    let alive = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    isLoadingRef.current = true;

    async function load() {
      console.log('[Microsite] Starting load, slug:', slug);
      console.log('[Microsite] Firestore db:', db ? 'initialized' : 'not initialized');
      console.log('[Microsite] Firebase config check:', {
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        hasApiKey: !!import.meta.env.VITE_FIREBASE_API_KEY,
        useEmulator: import.meta.env.VITE_USE_EMULATOR === 'true'
      });
      
      try {
        if (!slug) {
          console.warn('[Microsite] No restaurant slug provided, showing not found');
          setNotFound(true);
          setLoading(false);
          isLoadingRef.current = false;
          return;
        }

        // Set a timeout to prevent infinite loading
        timeoutId = setTimeout(() => {
          if (alive) {
            console.error('[Microsite] Loading timeout after 10 seconds');
            setError('Request timeout. Please check your connection and try again.');
            setNotFound(true);
            setLoading(false);
          }
        }, 10000);

        console.log('[Microsite] Fetching microsite document:', slug);
        const startTime = Date.now();
        
        // Query microsites collection to find the document with matching slug
        const siteDoc = await getDoc(doc(db, 'microsites', slug));
        
        const fetchTime = Date.now() - startTime;
        console.log(`[Microsite] Document fetch took ${fetchTime}ms`);
        console.log('[Microsite] Document exists:', siteDoc.exists());
        
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        if (!siteDoc.exists()) {
          console.warn('[Microsite] Restaurant page not found for slug:', slug);
          setNotFound(true);
          setLoading(false);
          return;
        }

        const data = siteDoc.data();
        
        // Check if microsite is inactive
        if (data?.status === 'inactive') {
          console.warn('[Microsite] Microsite is inactive for slug:', slug);
          setIsInactive(true);
          setLoading(false);
          return;
        }
        console.log('[Microsite] Restaurant data loaded:', { 
          hasData: !!data, 
          hasLocationId: !!data?.location_id,
          name: data?.name,
          logo_url: data?.logo_url,
          hero_image_url: data?.hero_image_url
        });
        console.log('[Microsite] Raw Firestore document data:', data);
        console.log('[Microsite] All document fields:', Object.keys(data || {}));

        let finalData: any = data;
        if (data?.location_id) {
          console.log('[Microsite] Fetching location data:', data.location_id);
          const locationStartTime = Date.now();
          const locationDoc = await getDoc(doc(db, 'locations', data.location_id));
          const locationFetchTime = Date.now() - locationStartTime;
          console.log(`[Microsite] Location fetch took ${locationFetchTime}ms`);
          
          if (locationDoc.exists()) {
            const locationData = locationDoc.data();
            console.log('[Microsite] Location data loaded:', { 
              hasData: !!locationData,
              name: locationData?.name 
            });
            // Merge location data with microsite custom fields (welcome text, hero image, logo)
            // IMPORTANT: Spread locationData first, then override with microsite-specific fields
            finalData = { 
              id: locationDoc.id, 
              ...locationData,
              // Microsite overrides: welcome text, hero image, and logo (only if set in microsite doc)
              // These override locationData fields if they exist in the microsite document
              welcome_title: data.welcome_title || undefined,
              welcome_description: data.welcome_description || undefined,
              hero_image_url: data.hero_image_url || locationData.image_url || undefined,
              // Logo URL should come from microsite doc, not location
              logo_url: (data.logo_url && data.logo_url.trim() !== '') ? data.logo_url : undefined,
            };
            console.log('[Microsite] Final data after merge:', {
              hasLogoUrl: !!finalData.logo_url,
              logoUrl: finalData.logo_url,
              hasHeroImageUrl: !!finalData.hero_image_url,
              heroImageUrl: finalData.hero_image_url
            });
          } else {
            console.warn('[Microsite] Location not found:', data.location_id);
            // Use microsite data with location_id as id if location doesn't exist
            finalData = { id: data.location_id, ...data };
          }
        } else {
          console.warn('[Microsite] No location_id in microsite data, using microsite data only');
          // If no location_id, we need at least some identifier
          finalData = { id: slug, ...data };
        }

        if (!alive) {
          console.log('[Microsite] Component unmounted, skipping state update');
          return;
        }

        console.log('[Microsite] Setting restaurant data:', { 
          hasData: !!finalData,
          hasId: !!finalData?.id,
          name: finalData?.name,
          logo_url: finalData?.logo_url,
          hero_image_url: finalData?.hero_image_url,
          rawMicrositeData: {
            logo_url: data?.logo_url,
            hero_image_url: data?.hero_image_url
          }
        });
        
        setRestaurant(finalData);
        setLoading(false);
        isLoadingRef.current = false;
      } catch (e) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        isLoadingRef.current = false;
        console.error('[Microsite] Error loading data:', e);
        console.error('[Microsite] Error details:', {
          message: e instanceof Error ? e.message : 'Unknown error',
          stack: e instanceof Error ? e.stack : undefined,
          name: e instanceof Error ? e.name : undefined
        });
        
        // Check for common Firestore errors
        if (e instanceof Error) {
          if (e.message.includes('PERMISSION_DENIED') || e.message.includes('permission')) {
            console.error('[Microsite] Firestore permission denied - check security rules');
            setError('Access denied. Please check your permissions.');
          } else if (e.message.includes('network') || e.message.includes('fetch')) {
            console.error('[Microsite] Network error - check connection and emulator status');
            setError('Network error. Please check your connection and try again.');
          } else if (e.message.includes('Maximum call stack') || e.message.includes('stack overflow')) {
            console.error('[Microsite] Stack overflow detected - possible infinite recursion');
            setError('An error occurred. Please refresh the page.');
          } else if (e.message.includes('IndexedDB') || e.message.includes('Indexed Database') || e.name === 'UnknownError') {
            console.warn('[Microsite] IndexedDB error - Firestore will use network-only mode');
            // IndexedDB errors shouldn't prevent the page from loading
            // Firestore will automatically fall back to network-only mode
            // Don't set error state, just log it
          } else {
            setError('Failed to load restaurant information. Please try again.');
          }
        }
        
        setNotFound(true);
        setLoading(false);
      }
    }

    load();
    return () => { 
      console.log('[Microsite] Cleanup: setting alive to false');
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      alive = false;
      isLoadingRef.current = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!restaurant || !slug || !isBrowser()) return;

    applyRestaurantPageSeo({
      slug,
      name: restaurant.name,
      description: restaurant.welcome_description,
      image: restaurant.hero_image_url || restaurant.logo_url,
      address: restaurant.address,
      phone: restaurant.contact?.phone,
      email: restaurant.contact?.email,
    });

    return () => {
      clearRestaurantPageSeo();
    };
  }, [restaurant, slug]);

  // Handle iframe auto-resize for widget iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify message origin for security (widget domains)
      const allowedOrigins = [
        'http://localhost:5175',
        'https://10seat-widget-staging.web.app',
        'https://reserve.10seat.com'
      ];

      if (!allowedOrigins.some(origin => event.origin.startsWith(origin))) {
        return;
      }

      if (event.data && event.data.type === '10seat-widget-height') {
        const height = event.data.height;
        if (typeof height === 'number' && height > 0) {
          // Update desktop iframe height
          if (widgetIframeRef.current) {
            widgetIframeRef.current.style.height = `${height}px`;
          }
          // Update mobile iframe height
          if (widgetIframeMobileRef.current) {
            widgetIframeMobileRef.current.style.height = `${height}px`;
          }
        }
      }

      // Payment open request from widget (needed when iframe is sandboxed without allow-popups)
      if (event.data && event.data.type === '10seat-open-payment') {
        const url = event.data.url;
        if (typeof url !== 'string') return;

        // Only allow known PSP checkout URLs (prevent arbitrary popup requests)
        const allowedUrlPrefixes = [
          'https://www.mollie.com/checkout/',
          'https://mollie.com/checkout/',
          'https://www.multisafepay.com/',
          'https://multisafepay.com/',
          'https://testpayv2.multisafepay.com/',
          'https://pay.multisafepay.com/',
          // Our own hosted checkout (MultiSafepay deposits and payment links)
          'https://pay.10seat.com/',
          'https://staging.pay.10seat.com/',
        ];
        if (!allowedUrlPrefixes.some(prefix => url.startsWith(prefix))) return;

        // Open in new tab/window from parent context (not sandboxed)
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Send language change to widget iframes when locale changes
  useEffect(() => {
    const sendLanguageToWidget = (iframe: HTMLIFrameElement | null) => {
      if (iframe?.contentWindow && locale) {
        try {
          iframe.contentWindow.postMessage(
            { type: '10seat-widget-language', locale },
            '*' // Widget domains are validated in widget
        );
        } catch (error) {
          console.warn('[Microsite] Failed to send language to widget:', error);
        }
      }
    };

    // Send to both desktop and mobile iframes
    sendLanguageToWidget(widgetIframeRef.current);
    sendLanguageToWidget(widgetIframeMobileRef.current);
  }, [locale]);

  const name = restaurant?.name || slug || 'Restaurant';
  const addressLine = restaurant ? fullAddress(restaurant) : '';
  const phoneVal = restaurant?.contact?.phone || '';
  const emailVal = restaurant?.contact?.email || '';
  const timezone = restaurant?.timezone || 'Europe/Brussels';
  
  // Memoize todayKey to prevent recalculation on every render
  // Note: This will still update when timezone changes, but won't recalculate unnecessarily
  const todayKey = useMemo(() => getTodayKey(timezone), [timezone]);
  
  // Convert restaurant data to LocationData format for compatibility (not used by widget but kept for potential future use)
  const locationData: LocationData | null = useMemo(() => restaurant ? {
    operating_hours: restaurant.operating_hours || restaurant.operatingHours,
    reservationSettings: {
      defaultReservationDuration: restaurant.reservationSettings?.defaultReservationDuration || 120
    },
    timezone: timezone
  } : null, [restaurant, timezone]);

  if (loading) {
    return (
      <div className="microsite-single-column">
        <div className="skeleton-card" aria-busy="true" aria-live="polite">
          Loading your reservation page…
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="microsite-single-column not-found">
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('errors.not_found_title')}</h1>
          <p className="text-lg text-gray-600 mb-4">{t('errors.not_found_message')}</p>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 font-semibold">{error}</p>
            </div>
          )}
          {import.meta.env.DEV && (
            <div className="mt-8 p-4 bg-gray-100 rounded-lg text-left text-sm">
              <p className="font-semibold mb-2">Debug Info:</p>
              <p>Path: {isBrowser() ? window.location.pathname : 'N/A'}</p>
              <p>Slug: {slug || 'Not found'}</p>
              <p>Expected format: /restaurant/[slug] or /[slug]</p>
              <p>Firebase Project: {import.meta.env.VITE_FIREBASE_PROJECT_ID || 'Not set'}</p>
              <p>Using Emulator: {import.meta.env.VITE_USE_EMULATOR === 'true' ? 'Yes' : 'No'}</p>
              {import.meta.env.VITE_USE_EMULATOR === 'true' && (
                <p className="text-orange-600 mt-2">⚠️ Make sure Firestore emulator is running on port 8082</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isInactive) {
    return (
      <div className="microsite-single-column not-found">
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('errors.inactive_title')}</h1>
          <p className="text-lg text-gray-600 mb-4">{t('errors.inactive_message')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="microsite-single-column">
      {/* Header */}
      <header className="microsite-header">
        <div className="container mx-auto px-4 py-4 md:py-6 flex items-center justify-between">
          {restaurant?.logo_url ? (
            <img 
              src={restaurant.logo_url} 
              alt={name}
              className="h-12 md:h-16 w-auto object-contain"
              onError={(e) => {
                console.error('[Microsite] Failed to load logo image:', restaurant.logo_url);
                // Fallback to restaurant name if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) {
                  fallback.style.display = 'block';
                }
              }}
            />
          ) : null}
          {!restaurant?.logo_url && (
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">{name}</h1>
          )}
          <LanguageSelector />
        </div>
      </header>

      {/* Hero Section with Image */}
      <section className="hero-section">
        <div className="hero-image-container">
          {restaurant?.hero_image_url || restaurant?.image_url ? (
            <img 
              src={restaurant.hero_image_url || restaurant.image_url} 
              alt={name}
              className="hero-image"
            />
          ) : (
            <div className="hero-image-placeholder">
              <div className="hero-image-gradient">
                {name.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Content: Two Column Layout */}
      <section className="main-content-section">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="main-content-grid">
            {/* Left Column: 70% - Welcome Text + Opening Hours */}
            <div className="main-content-left">
              {/* Welcome Text */}
              <div className="welcome-section">
                <h2 className="welcome-title">
                  {restaurant?.welcome_title || `Welcome to ${name}`}
                </h2>
                {restaurant?.welcome_description ? (
                  <p className="welcome-description">
                    {restaurant.welcome_description}
                  </p>
                ) : (
                  <p className="welcome-description">
                    Experience exceptional dining in a warm and inviting atmosphere. We pride ourselves on serving 
                    authentic cuisine crafted with the finest ingredients and passion for culinary excellence. 
                    Whether you're celebrating a special occasion or enjoying a casual meal, we look forward to welcoming you.
                  </p>
                )}
              </div>

              {/* Booking Widget - Mobile Only (shown before info on mobile) */}
              <div className="booking-section-mobile">
                <div className="booking-form-header">
                  <h2 className="booking-form-title">
                    {t('booking.title')}
                  </h2>
                  <p className="booking-form-subtitle">
                    {t('booking.subtitle')}
                  </p>
                </div>
                
                <div className="booking-form-container">
                  {restaurant?.id ? (
                    <iframe
                      ref={widgetIframeMobileRef}
                      id="tenseat-widget-iframe-mobile"
                      src={getWidgetUrl(restaurant.id, name, locale)}
                      title={t('booking.title')}
                      className="w-full border-0 rounded-lg"
                      style={{ minHeight: '600px', width: '100%' }}
                      loading="lazy"
                      sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                      onLoad={() => {
                        // Send initial language when iframe loads
                        if (widgetIframeMobileRef.current?.contentWindow && locale) {
                          widgetIframeMobileRef.current.contentWindow.postMessage(
                            { type: '10seat-widget-language', locale },
                            '*'
                          );
                        }
                      }}
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Loading booking widget...
                    </div>
                  )}
                </div>
              </div>

              {/* Opening Hours and Contact Info - Side by Side (40% / 60%) */}
              <div className="info-grid-left">
                {/* Opening Hours - 40% */}
                <div className="hours-section-left">
                  <h3 className="hours-section-title">
                    <Clock className="w-5 h-5" />
                    {t('restaurant.opening_hours')}
                  </h3>
                  <div className="hours-list">
                    {DAY_KEYS.map((key, idx) => {
                      const title = DAY_TITLES[idx];
                      const hoursData = restaurant ? renderDayHours(key, restaurant) : '—';
                      const isToday = key === todayKey;
                      const isClosed = hoursData === 'Closed';
                      const hasShifts = typeof hoursData === 'object' && 'shifts' in hoursData;
                      
                      return (
                        <div 
                          key={key} 
                          className={`hours-row ${isToday ? 'hours-row-today' : ''}`}
                        >
                          <span className="hours-day-name">
                            {title}
                            {isToday && (
                              <span className="today-badge">{t('status.today')}</span>
                            )}
                          </span>
                          <span className="hours-time">
                            {isClosed ? (
                              <span className="hours-closed">{t('status.closed')}</span>
                            ) : !hasShifts ? (
                              <span className="hours-empty">—</span>
                            ) : (
                              <span className="hours-shifts">
                                {hoursData.shifts.map((shift, i) => {
                                  if (DEBUG && i === 0) {
                                    log(`[Hours Display] ${key}: Rendering ${hoursData.shifts.length} shifts`);
                                  }
                                  const timeRange = `${formatTime(shift.open)}–${formatTime(shift.close)}`;
                                  return (
                                    <span key={i} className="hours-shift-item">
                                      {shift.name && (
                                        <span className="hours-shift-name">{shift.name}</span>
                                      )}
                                      <span className="hours-shift-time">{timeRange}</span>
                                    </span>
                                  );
                                })}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Contact Information with Map - 60% */}
                <div className="contact-section-left">
                  <h3 className="contact-section-title">
                    {t('restaurant.contact_information')}
                  </h3>
                  <div className="contact-list">
                    {addressLine && (
                      <div className="contact-item">
                        <MapPin className="contact-icon" aria-hidden="true" />
                        <span>{addressLine}</span>
                      </div>
                    )}
                    {phoneVal && (
                      <div className="contact-item">
                        <Phone className="contact-icon" aria-hidden="true" />
                        <a href={`tel:${phoneVal}`} aria-label={`Call ${name}`}>
                          {phoneVal}
                        </a>
                      </div>
                    )}
                    {emailVal && (
                      <div className="contact-item">
                        <Mail className="contact-icon" aria-hidden="true" />
                        <a href={`mailto:${emailVal}`} aria-label={`Email ${name}`}>
                          {emailVal}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Google Maps Embed - No API key needed for basic embed */}
                  {addressLine && (
                    <div className="map-container">
                      <iframe
                        src={`https://www.google.com/maps?q=${encodeURIComponent(addressLine)}&output=embed`}
                        width="100%"
                        height="250"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="google-map-embed"
                        title={`Map showing location of ${name}`}
                      />
                      
                      {/* Get Directions Button */}
                      <button
                        type="button"
                        onClick={() => {
                          if (!isBrowser() || !addressLine) return;
                          
                          const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                          const isAndroid = /Android/i.test(navigator.userAgent);
                          
                          // Use Google Maps web URL which will prompt to open in app on mobile
                          // iOS: will prompt to open in Apple Maps or Google Maps app if installed
                          // Android: will prompt to open in Google Maps app if installed
                          const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressLine)}`;
                          
                          // For iOS, try to open Apple Maps first, but Google Maps web will also work
                          if (isIOS) {
                            // Try Apple Maps URL first
                            const appleMapsUrl = `maps://maps.apple.com/?daddr=${encodeURIComponent(addressLine)}`;
                            window.location.href = appleMapsUrl;
                            // If Apple Maps isn't available, the page will stay and we can fallback
                            setTimeout(() => {
                              window.open(mapsUrl, '_blank');
                            }, 1000);
                          } else if (isAndroid) {
                            // Android: Google Maps web will prompt to open in app
                            window.open(mapsUrl, '_blank');
                          } else {
                            // Desktop or other: open in new tab
                            window.open(mapsUrl, '_blank');
                          }
                        }}
                        className="get-directions-button"
                        aria-label={`${t('restaurant.get_directions')} to ${name}`}
                      >
                        <MapPin className="w-5 h-5" />
                        {t('restaurant.get_directions')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: 30% - Booking Widget (Desktop Only) */}
            <div className="main-content-right">
              <div className="booking-form-sticky">
                <div className="booking-form-header">
                  <h2 className="booking-form-title">
                    {t('booking.title')}
                  </h2>
                  <p className="booking-form-subtitle">
                    {t('booking.subtitle')}
                  </p>
                </div>
                
                <div className="booking-form-container">
                  {restaurant?.id ? (
                    <iframe
                      ref={widgetIframeRef}
                      id="tenseat-widget-iframe"
                      src={getWidgetUrl(restaurant.id, name, locale)}
                      title={t('booking.title')}
                      className="w-full border-0 rounded-lg"
                      style={{ minHeight: '600px', width: '100%' }}
                      loading="lazy"
                      sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                      onLoad={() => {
                        // Send initial language when iframe loads
                        if (widgetIframeRef.current?.contentWindow && locale) {
                          widgetIframeRef.current.contentWindow.postMessage(
                            { type: '10seat-widget-language', locale },
                            '*'
                          );
                        }
                      }}
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Loading booking widget...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="microsite-footer">
        <div className="container mx-auto px-4 py-6 text-center">
          <p className="footer-text">
            {t('common.powered_by')}{' '}
            <a
              href={TEN_SEAT_MARKETING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-brand footer-brand-link"
            >
              {t('common.app_name')}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
